import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ButtonStandard, ModalBase, Text, Wizard, WizardStep } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import ApprovalChainStep from "./components/ApprovalChainStep";
import CycleBudgetStep from "./components/CycleBudgetStep";
import CycleDetailsStep from "./components/CycleDetailsStep";
import EmployeeSelectionStep from "./components/EmployeeSelectionStep";
import ReviewLaunchStep from "./components/ReviewLaunchStep";

import ErrorPage from "../../../pages/Error";

import {
	CompensationWizardData,
	CycleDetailsData,
	allotmentBudgetSchema,
	approvalChainSchema,
	cycleDetailsSchema,
	useCompensationWizardData,
} from "./types";

import { useCompanySettings } from "../../../api/hooks/defs/company";
import { useCreateCompensationCycle } from "../../../api/hooks/defs/compensation";
import { getFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";

import { currentUserQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";
import { rbacPermissionsQuery } from "../../../api/queries/defs/rbac";
import { getUserSelectionCount } from "../../../api/services/defs/user-selection";

import { dateToTimeInTimeZone } from "../utils";

/** React Router path for `CompensationLanding` (see `routes.tsx` → `path: "compensation"`). */
const COMPENSATION_LANDING_PATH = "/compensation/";

export const compensationCreateLoader = () => {
	return dataLoader([[currentUserQuery]]);
};

const PlaceholderStep = () => (
	<div className="flex items-center justify-center py-12">
		<Text size="paragraph-2" color="primary-2">
			Coming soon
		</Text>
	</div>
);

type WizardUnsavedChangesGuardProps = {
	userSelectionId: string | null;
	setIsSafe: () => void;
	setIsUnsafe: () => void;
};

const WizardUnsavedChangesGuard = ({ userSelectionId, setIsSafe, setIsUnsafe }: WizardUnsavedChangesGuardProps) => {
	const wizardData = useCompensationWizardData();
	const hasUnsavedChanges = useMemo(() => {
		const cycleDetails = wizardData["cycle-details"];
		const cycleBudget = wizardData["cycle-budget"];
		const approvalChain = wizardData["approval-chain"];

		return Boolean(
			userSelectionId ||
			cycleDetails?.name ||
			cycleDetails?.startDate ||
			cycleDetails?.dueDate ||
			(cycleBudget?.allotmentIncreaseFixed ?? 0) > 0 ||
			Object.keys(approvalChain?.managerOverrides ?? {}).length > 0
		);
	}, [userSelectionId, wizardData]);

	useEffect(() => {
		if (hasUnsavedChanges) {
			setIsUnsafe();
			return;
		}

		setIsSafe();
	}, [hasUnsavedChanges, setIsSafe, setIsUnsafe]);

	return null;
};

const CompensationCreateWizard = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: companySettings } = useCompanySettings();
	const companyTimeZone = companySettings?.default_timezone || "America/Eastern";
	const [userSelectionId, setUserSelectionId] = useState<string | null>(null);
	const [showExitModal, setShowExitModal] = useState(false);
	const [showEmployeeSelectionError, setShowEmployeeSelectionError] = useState(false);
	const [suppressUnsavedChanges, setSuppressUnsavedChanges] = useState(false);
	const { setIsSafe, setIsUnsafe } = useUnsavedChanges({
		message: "You have unsaved compensation cycle changes.",
		disabled: suppressUnsavedChanges,
	});

	const { mutate: createCycle, isPending: isCreatingCycle } = useCreateCompensationCycle({
		onSuccess: (createdCycle) => {
			setIsSafe();
			void queryClient.invalidateQueries({ queryKey: ["compensation-cycles"] });
			navigate(`/compensation/cycles/${createdCycle._id}/cycle-overview`);
		},
		onError: () => {
			setSuppressUnsavedChanges(false);
		},
	});

	const featureFlagsQuery = useQuery({
		...getFeatureFlagQuery(),
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});

	const compensationEnabled = useMemo((): boolean | undefined => {
		const response = featureFlagsQuery.data;
		if (!response) return undefined;
		const flag = response.feature_flags?.find((f) => f._id === "ff-compensation");
		if (!flag) return undefined;
		return flag.value as boolean;
	}, [featureFlagsQuery.data]);

	const {
		data: permissions,
		isLoading: isCompensationPermissionsLoading,
		isError: isCompensationPermissionsError,
		isSuccess: isCompensationPermissionsSuccess,
	} = useQuery({
		...rbacPermissionsQuery("compensation"),
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});

	const userCanCreateCompensationCycle = isCompensationPermissionsSuccess
		? Boolean(permissions?.includes("compensation:createCycle"))
		: undefined;

	if (isCompensationPermissionsLoading || featureFlagsQuery.isLoading || compensationEnabled === undefined) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}
	if (isCompensationPermissionsError) {
		return <ErrorPage code={403} />;
	}
	if (compensationEnabled === false) {
		return <ErrorPage code={403} />;
	}

	if (userCanCreateCompensationCycle === false) {
		return <ErrorPage code={403} />;
	}

	const handleComplete = async (wizardData: CompensationWizardData) => {
		const cycleDetails = wizardData["cycle-details"];
		const cycleBudget = wizardData["cycle-budget"];
		const cycleManagers = wizardData["cycle-managers"];
		if (!cycleDetails || !cycleBudget || !cycleManagers || !userSelectionId) return;

		setSuppressUnsavedChanges(true);
		setIsSafe();
		createCycle({
			name: cycleDetails.name,
			startDate: dateToTimeInTimeZone(cycleDetails.startDate, companyTimeZone),
			dueDate: dateToTimeInTimeZone(cycleDetails.dueDate, companyTimeZone),
			approvalType: cycleManagers.approvalType,
			allotmentIncrease: cycleBudget.allotmentIncreaseFixed ?? 0,
			userSelectionId: userSelectionId,
			totalEligibleCompensation: cycleBudget.totalEligibleCompensation ?? 0,
			managerOverrides: cycleManagers?.managerOverrides || null,
		});
	};

	return (
		<div className="h-[calc(100vh-64px)]">
			<ModalBase
				isOpen={showExitModal}
				onClose={() => {
					setShowExitModal(false);
					setSuppressUnsavedChanges(false);
				}}
				dangerouslySetClassName="w-[432px]"
			>
				<div className="flex flex-col gap-6 px-6 pb-6">
					<div className="flex flex-col gap-0">
						<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
							Exit Wizard
						</Text>
						<div className="flex flex-col gap-0 pl-6 pr-6">
							<Text size="paragraph-2" weight="regular" dangerouslySetClassName="text-center">
								Your compensation cycle configuration will not be saved. Are you sure you want to exit?
							</Text>
						</div>
					</div>
					<div className="flex w-full flex-wrap justify-center gap-3">
						<ButtonStandard
							size="large"
							variant="outline"
							color="neutral"
							dangerouslySetClassName="w-auto px-[16px]"
							onClick={() => {
								setShowExitModal(false);
								setSuppressUnsavedChanges(false);
							}}
						>
							Cancel
						</ButtonStandard>
						<ButtonStandard
							size="large"
							color="primary"
							dangerouslySetClassName="w-auto px-[16px]"
							onClick={() => {
								setSuppressUnsavedChanges(true);
								setIsSafe();
								navigate(COMPENSATION_LANDING_PATH);
							}}
						>
							Yes, Exit Wizard
						</ButtonStandard>
					</div>
				</div>
			</ModalBase>
			<Wizard
				id="compensation-create"
				title="Creation Steps"
				submitLabel="Create Compensation Cycle"
				isSubmitting={isCreatingCycle}
				onComplete={(data) => handleComplete(data as CompensationWizardData)}
				onExit={() => {
					setSuppressUnsavedChanges(true);
					setShowExitModal(true);
				}}
			>
				<WizardStep
					id="cycle-details"
					title="Cycle Details"
					schema={cycleDetailsSchema}
					defaultValues={{ name: "", startDate: null, dueDate: null } as unknown as CycleDetailsData}
				>
					{() => (
						<>
							<WizardUnsavedChangesGuard
								userSelectionId={userSelectionId}
								setIsSafe={setIsSafe}
								setIsUnsafe={setIsUnsafe}
							/>
							<CycleDetailsStep />
						</>
					)}
				</WizardStep>

				<WizardStep
					id="employee-selection"
					title="Employee Selection"
					beforeNext={async () => {
						if (!userSelectionId) {
							setShowEmployeeSelectionError(true);
							return false;
						}
						const data = await getUserSelectionCount(userSelectionId);
						if ((data?.count ?? 0) === 0) {
							setShowEmployeeSelectionError(true);
							return false;
						}
						setShowEmployeeSelectionError(false);
						return true;
					}}
				>
					{() => (
						<>
							<WizardUnsavedChangesGuard
								userSelectionId={userSelectionId}
								setIsSafe={setIsSafe}
								setIsUnsafe={setIsUnsafe}
							/>
							<EmployeeSelectionStep
								userSelectionId={userSelectionId}
								onSelectionCreated={setUserSelectionId}
								showSelectionError={showEmployeeSelectionError}
							/>
						</>
					)}
				</WizardStep>

				<WizardStep
					id="cycle-budget"
					title="Cycle Budget"
					schema={allotmentBudgetSchema}
					defaultValues={{ totalEligibleCompensation: 0 }}
				>
					{() =>
						userSelectionId ? (
							<>
								<WizardUnsavedChangesGuard
									userSelectionId={userSelectionId}
									setIsSafe={setIsSafe}
									setIsUnsafe={setIsUnsafe}
								/>
								<CycleBudgetStep userSelectionId={userSelectionId} />
							</>
						) : (
							<PlaceholderStep />
						)
					}
				</WizardStep>

				<WizardStep
					id="cycle-managers"
					title="Cycle Managers"
					schema={approvalChainSchema}
					defaultValues={{ approvalType: "manager", managerOverrides: {} }}
				>
					{() =>
						userSelectionId ? (
							<>
								<WizardUnsavedChangesGuard
									userSelectionId={userSelectionId}
									setIsSafe={setIsSafe}
									setIsUnsafe={setIsUnsafe}
								/>
								<ApprovalChainStep userSelectionId={userSelectionId} />
							</>
						) : (
							<PlaceholderStep />
						)
					}
				</WizardStep>

				<WizardStep id="preview-finalize" title="Preview and Finalize">
					{() =>
						userSelectionId ? (
							<>
								<WizardUnsavedChangesGuard
									userSelectionId={userSelectionId}
									setIsSafe={setIsSafe}
									setIsUnsafe={setIsUnsafe}
								/>
								<ReviewLaunchStep userSelectionId={userSelectionId} />
							</>
						) : (
							<PlaceholderStep />
						)
					}
				</WizardStep>
			</Wizard>
		</div>
	);
};

export default CompensationCreateWizard;
